/**
 * Product Features — Transform-Based Cyclic Carousel & Lightbox
 * Silverback Moto Co. — v6
 *
 * Architecture:
 * - Viewport is a clipping frame (no native scroll).
 * - Track is translated left/right via CSS transform.
 * - Cyclic via edge clones. When track lands on a clone, JS silently
 *   repositions to the real equivalent (transition disabled for the jump).
 * - Indicator updates are synchronous with each goTo() call.
 * - translateToCursor() uses offsetLeft directly for reliable positioning,
 *   eliminating any coordinate math drift.
 *
 * Interactions:
 * - Click center card  → lightbox
 * - Click partial card → navigate to it
 * - Click clone card   → animate through the clone (natural direction)
 * - Keyboard ← / →     → navigate (when carousel is hovered)
 * - Touch swipe        → drag to nav (mobile)
 * - Mouse drag         → drag to nav (desktop)
 * - Auto-rotate        → every 4.5s, pauses on hover
 */
(function () {
  'use strict';

  document.querySelectorAll('[data-product-features]').forEach(initSection);

  function initSection(section) {
    var viewport = section.querySelector('[data-features-viewport]');
    var track = section.querySelector('[data-features-track]');
    if (!viewport || !track) return;

    var realCards = [].slice.call(track.querySelectorAll('[data-feature-card]'));
    var TOTAL = realCards.length;
    if (TOTAL === 0) return;

    // ─────────────────────────────────────────────
    // EDGE CLONES for cyclic behavior
    // ─────────────────────────────────────────────
    if (TOTAL > 1) {
      var firstClone = realCards[0].cloneNode(true);
      var lastClone = realCards[TOTAL - 1].cloneNode(true);
      firstClone.setAttribute('data-clone', 'tail');
      lastClone.setAttribute('data-clone', 'head');
      firstClone.removeAttribute('data-feature-card');
      lastClone.removeAttribute('data-feature-card');
      // Prepend the last-card clone (as head) so it sits LEFT of card 0
      track.insertBefore(lastClone, track.firstChild);
      // Append the first-card clone (as tail) so it sits RIGHT of card N-1
      track.appendChild(firstClone);
    }

    // DOM layout when TOTAL > 1: [headClone, card0, card1, ..., cardN-1, tailClone]
    // Virtual cursor:
    //   cursor = -1   → head clone (visually shows last card)
    //   cursor = 0..N-1 → real cards
    //   cursor = N    → tail clone (visually shows first card)

    var AUTO_MS = 4500;
    var ANIM_MS = 450;
    var cursor = 0;
    var autoTimer = null;
    var openLightbox = function () {};
    var isAnimating = false;

    // ─────────────────────────────────────────────
    // LAYOUT MEASUREMENT
    // ─────────────────────────────────────────────
    var cardWidth = 0;
    var gap = 0;
    var stride = 0;

    function measureLayout() {
      if (track.children.length === 0) return;
      var firstChild = track.children[0];
      cardWidth = firstChild.getBoundingClientRect().width;

      if (track.children.length > 1) {
        var firstRect = firstChild.getBoundingClientRect();
        var secondRect = track.children[1].getBoundingClientRect();
        gap = secondRect.left - firstRect.right;
      } else {
        gap = 0;
      }
      stride = cardWidth + gap;
    }

    // ─────────────────────────────────────────────
    // TRANSLATE TRACK TO CURRENT CURSOR
    // Uses offsetLeft directly for reliable positioning.
    // ─────────────────────────────────────────────
    function translateToCursor(animate) {
      var domIdx;
      if (TOTAL > 1) {
        domIdx = cursor + 1; // cursor -1 → DOM 0, cursor 0 → DOM 1, etc.
      } else {
        domIdx = 0;
      }

      var targetEl = track.children[domIdx];
      if (!targetEl) return;

      var cardLeftInTrack = targetEl.offsetLeft;
      var targetX = -(cardLeftInTrack + targetEl.offsetWidth / 2 - viewport.clientWidth / 2);

      if (animate) {
        track.classList.add('is-animating');
      } else {
        track.classList.remove('is-animating');
        // Force reflow so the class removal takes effect before the new transform
        void track.offsetWidth;
      }
      track.style.transform = 'translateX(' + targetX + 'px)';
    }

    // ─────────────────────────────────────────────
    // GO TO CURSOR POSITION
    // ─────────────────────────────────────────────
    function goTo(newCursor, animate) {
      if (isAnimating) return;

      // Guard: no-op if same cursor
      if (newCursor === cursor && animate !== false) {
        translateToCursor(false);
        return;
      }

      cursor = newCursor;

      // Update dashes immediately based on real-card equivalent
      var realIdx;
      if (cursor === -1) realIdx = TOTAL - 1;
      else if (cursor === TOTAL) realIdx = 0;
      else realIdx = cursor;
      updateDashes(realIdx);

      if (animate === false) {
        translateToCursor(false);
        return;
      }

      // Animated transition
      isAnimating = true;

      var settled = false;
      var onSettle = function () {
        if (settled) return;
        settled = true;
        track.removeEventListener('transitionend', onTransitionEnd);
        isAnimating = false;

        if (TOTAL > 1) {
          if (cursor === -1) {
            // Landed on head clone → silently snap to real last card
            cursor = TOTAL - 1;
            translateToCursor(false);
          } else if (cursor === TOTAL) {
            // Landed on tail clone → silently snap to real first card
            cursor = 0;
            translateToCursor(false);
          }
        }
      };

      var onTransitionEnd = function (e) {
        // Only respond to the track's own transform transition,
        // not bubbled transitions from descendants
        if (e.target !== track) return;
        if (e.propertyName !== 'transform') return;
        onSettle();
      };

      track.addEventListener('transitionend', onTransitionEnd);

      // Safety fallback in case transitionend doesn't fire
      setTimeout(onSettle, ANIM_MS + 100);

      translateToCursor(true);
    }

    function goNext() { goTo(cursor + 1, true); }
    function goPrev() { goTo(cursor - 1, true); }
    function goToRealIdx(realIdx) { goTo(realIdx, true); }

    // ─────────────────────────────────────────────
    // AUTO-ROTATION
    // ─────────────────────────────────────────────
    function startAuto() {
      stopAuto();
      if (TOTAL <= 1) return;
      autoTimer = setInterval(goNext, AUTO_MS);
    }

    function stopAuto() {
      if (autoTimer) { clearInterval(autoTimer); autoTimer = null; }
    }

    // ─────────────────────────────────────────────
    // DASH INDICATORS
    // ─────────────────────────────────────────────
    var indicatorWrap = section.querySelector('[data-indicators]');
    var dashes = indicatorWrap ? indicatorWrap.querySelectorAll('[data-dash]') : [];

    function updateDashes(realIdx) {
      for (var i = 0; i < dashes.length; i++) {
        if (i === realIdx) dashes[i].classList.add('is-active');
        else dashes[i].classList.remove('is-active');
      }
    }

    if (indicatorWrap) {
      indicatorWrap.addEventListener('click', function (e) {
        var dash = e.target.closest('[data-dash]');
        if (!dash) return;
        if (isAnimating) return;
        var target = parseInt(dash.getAttribute('data-dash'), 10);
        stopAuto();
        goToRealIdx(target);
        setTimeout(startAuto, ANIM_MS + 150);
      });
    }

    // ─────────────────────────────────────────────
    // CARD / CLONE CLICK
    // ─────────────────────────────────────────────
    section.addEventListener('click', function (e) {
      // Clone click → animate through the clone for natural direction
      var clone = e.target.closest('[data-clone]');
      if (clone) {
        if (isAnimating) return;
        var which = clone.getAttribute('data-clone');
        stopAuto();
        if (which === 'head') {
          // Head clone is LEFT of card 0 → animate backward
          goTo(-1, true);
        } else {
          // Tail clone is RIGHT of card N-1 → animate forward
          goTo(TOTAL, true);
        }
        setTimeout(startAuto, ANIM_MS + 150);
        return;
      }

      // Real card click
      var card = e.target.closest('[data-feature-card]');
      if (!card) return;
      if (isAnimating) return;
      var idx = parseInt(card.getAttribute('data-feature-card'), 10);
      if (idx === cursor) {
        openLightbox(cursor);
      } else {
        stopAuto();
        goToRealIdx(idx);
        setTimeout(startAuto, ANIM_MS + 150);
      }
    });

    // ─────────────────────────────────────────────
    // KEYBOARD NAVIGATION (when hovered)
    // ─────────────────────────────────────────────
    var hasHover = false;
    section.addEventListener('mouseenter', function () { hasHover = true; });
    section.addEventListener('mouseleave', function () { hasHover = false; });

    document.addEventListener('keydown', function (e) {
      if (!hasHover) return;
      var lb = section.querySelector('[data-lb]');
      if (lb && lb.classList.contains('is-open')) return;
      if (isAnimating) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        stopAuto();
        goPrev();
        setTimeout(startAuto, ANIM_MS + 150);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        stopAuto();
        goNext();
        setTimeout(startAuto, ANIM_MS + 150);
      }
    });

    // ─────────────────────────────────────────────
    // TOUCH DRAG (mobile)
    // ─────────────────────────────────────────────
    var touchStartX = 0;
    var touchCurrentX = 0;
    var isTouching = false;
    var touchDragOffset = 0;

    viewport.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1 || isAnimating) return;
      isTouching = true;
      touchStartX = touchCurrentX = e.touches[0].clientX;
      touchDragOffset = 0;
      stopAuto();
      track.classList.remove('is-animating');
    }, { passive: true });

    viewport.addEventListener('touchmove', function (e) {
      if (!isTouching || e.touches.length !== 1) return;
      touchCurrentX = e.touches[0].clientX;
      touchDragOffset = touchCurrentX - touchStartX;

      // Live drag: use current cursor's real-DOM position + drag offset
      var domIdx = TOTAL > 1 ? cursor + 1 : cursor;
      var targetEl = track.children[domIdx];
      if (!targetEl) return;
      var baseX = -(targetEl.offsetLeft + targetEl.offsetWidth / 2 - viewport.clientWidth / 2);
      track.style.transform = 'translateX(' + (baseX + touchDragOffset) + 'px)';
    }, { passive: true });

    viewport.addEventListener('touchend', function () {
      if (!isTouching) return;
      isTouching = false;

      var threshold = cardWidth * 0.18;
      if (touchDragOffset < -threshold) {
        goNext();
      } else if (touchDragOffset > threshold) {
        goPrev();
      } else {
        goTo(cursor, true);
      }
      setTimeout(startAuto, ANIM_MS + 150);
    });

    // ─────────────────────────────────────────────
    // MOUSE DRAG (desktop)
    // ─────────────────────────────────────────────
    var mouseDown = false;
    var mouseStartX = 0;
    var mouseCurrX = 0;
    var mouseDragOffset = 0;

    viewport.addEventListener('mousedown', function (e) {
      if (isAnimating) return;
      // Don't start drag on clone clicks — let the click handler deal with those
      if (e.target.closest('[data-clone]')) return;
      mouseDown = true;
      mouseStartX = mouseCurrX = e.clientX;
      mouseDragOffset = 0;
      stopAuto();
      track.classList.remove('is-animating');
      viewport.style.cursor = 'grabbing';
      e.preventDefault();
    });

    window.addEventListener('mousemove', function (e) {
      if (!mouseDown) return;
      mouseCurrX = e.clientX;
      mouseDragOffset = mouseCurrX - mouseStartX;

      var domIdx = TOTAL > 1 ? cursor + 1 : cursor;
      var targetEl = track.children[domIdx];
      if (!targetEl) return;
      var baseX = -(targetEl.offsetLeft + targetEl.offsetWidth / 2 - viewport.clientWidth / 2);
      track.style.transform = 'translateX(' + (baseX + mouseDragOffset) + 'px)';
    });

    window.addEventListener('mouseup', function () {
      if (!mouseDown) return;
      mouseDown = false;
      viewport.style.cursor = '';

      var threshold = cardWidth * 0.18;
      if (mouseDragOffset < -threshold) {
        goNext();
      } else if (mouseDragOffset > threshold) {
        goPrev();
      } else {
        goTo(cursor, true);
      }
      setTimeout(startAuto, ANIM_MS + 150);
    });

    viewport.style.cursor = 'grab';

    // ─────────────────────────────────────────────
    // HOVER PAUSE
    // ─────────────────────────────────────────────
    section.addEventListener('mouseenter', stopAuto);
    section.addEventListener('mouseleave', function () {
      if (!mouseDown) startAuto();
    });

    // ─────────────────────────────────────────────
    // RESIZE
    // ─────────────────────────────────────────────
    var resizeRaf;
    window.addEventListener('resize', function () {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        measureLayout();
        translateToCursor(false);
      });
    });

    // ─────────────────────────────────────────────
    // INIT
    // ─────────────────────────────────────────────
    function init() {
      requestAnimationFrame(function () {
        measureLayout();
        cursor = 0;
        updateDashes(0);
        translateToCursor(false);
        startAuto();
      });
    }

    init();

    // Re-measure after all images load (affects layout if aspect ratios shift)
    window.addEventListener('load', function () {
      measureLayout();
      translateToCursor(false);
    });

    // ═════════════════════════════════════════════
    // LIGHTBOX (unchanged from v4)
    // ═════════════════════════════════════════════
    (function () {
      var lb = section.querySelector('[data-lb]');
      if (!lb) return;

      var bg = lb.querySelector('[data-lb-bg]');
      var xBtn = lb.querySelector('[data-lb-x]');
      var vp = lb.querySelector('[data-lb-vp]');
      var lbTrack = lb.querySelector('[data-lb-track]');
      var prevLb = lb.querySelector('[data-lb-prev]');
      var nextLb = lb.querySelector('[data-lb-next]');
      var counter = lb.querySelector('[data-lb-count]');
      var slides = lb.querySelectorAll('.product-features__lb-slide');
      var lbTotal = slides.length;
      var lbIdx = 0;

      var pinchOn = false, pinchD0 = 0, pScale = 1, pImg = null;
      var lbX0 = 0, lbXN = 0, lbSw = false;

      openLightbox = function (idx) {
        lbIdx = Math.max(0, Math.min(idx, lbTotal - 1));
        resetZoom();
        lb.classList.add('is-open');
        lb.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        stopAuto();
        lbTrack.style.transition = 'none';
        lbTrack.style.transform = 'translateX(' + (-lbIdx * 100) + 'vw)';
        requestAnimationFrame(function () {
          lbTrack.style.transition = 'transform 0.35s ease';
        });
        updC();
      };

      function closeLb() {
        lb.classList.remove('is-open');
        lb.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        resetZoom();
        startAuto();
      }

      function goLb(i) {
        i = Math.max(0, Math.min(i, lbTotal - 1));
        lbIdx = i;
        resetZoom();
        lbTrack.style.transition = 'transform 0.35s ease';
        lbTrack.style.transform = 'translateX(' + (-i * 100) + 'vw)';
        updC();
      }

      function resetZoom() {
        for (var i = 0; i < slides.length; i++) {
          var img = slides[i].querySelector('.product-features__lb-img');
          if (img) img.style.transform = '';
        }
        pScale = 1;
      }

      function updC() {
        if (counter) counter.textContent = (lbIdx + 1) + ' / ' + lbTotal;
      }

      function hyp(a, b) {
        return Math.sqrt(Math.pow(b.clientX - a.clientX, 2) + Math.pow(b.clientY - a.clientY, 2));
      }

      if (xBtn) xBtn.addEventListener('click', function (e) { e.stopPropagation(); closeLb(); });
      if (bg) bg.addEventListener('click', closeLb);
      if (vp) vp.addEventListener('click', function (e) {
        var s = e.target.closest('.product-features__lb-slide');
        if (!s) { closeLb(); return; }
        if (!e.target.closest('.product-features__lb-img-wrap') &&
          !e.target.closest('.product-features__lb-cap')) closeLb();
      });

      if (prevLb) prevLb.addEventListener('click', function (e) { e.stopPropagation(); goLb(lbIdx - 1); });
      if (nextLb) nextLb.addEventListener('click', function (e) { e.stopPropagation(); goLb(lbIdx + 1); });

      document.addEventListener('keydown', function (e) {
        if (!lb.classList.contains('is-open')) return;
        if (e.key === 'Escape') closeLb();
        if (e.key === 'ArrowLeft') goLb(lbIdx - 1);
        if (e.key === 'ArrowRight') goLb(lbIdx + 1);
      });

      if (vp) {
        vp.addEventListener('touchstart', function (e) {
          if (e.touches.length === 2) {
            pinchOn = true;
            pinchD0 = hyp(e.touches[0], e.touches[1]);
            pImg = slides[lbIdx] ? slides[lbIdx].querySelector('.product-features__lb-img') : null;
            return;
          }
          lbX0 = lbXN = e.touches[0].clientX;
          lbSw = true;
          lbTrack.style.transition = 'none';
        }, { passive: true });

        vp.addEventListener('touchmove', function (e) {
          if (pinchOn && e.touches.length === 2 && pImg) {
            pScale = Math.max(1, Math.min(5, hyp(e.touches[0], e.touches[1]) / pinchD0));
            pImg.style.transform = 'scale(' + pScale + ')';
            return;
          }
          if (!lbSw || e.touches.length > 1) return;
          lbXN = e.touches[0].clientX;
          var d = lbXN - lbX0;
          lbTrack.style.transform = 'translateX(' + (-(lbIdx * window.innerWidth) + d) + 'px)';
        }, { passive: true });

        vp.addEventListener('touchend', function () {
          if (pinchOn) {
            pinchOn = false;
            if (pScale < 1.2 && pImg) { pImg.style.transform = ''; pScale = 1; }
            return;
          }
          if (!lbSw) return;
          lbSw = false;
          lbTrack.style.transition = 'transform 0.35s ease';
          var diff = lbX0 - lbXN;
          if (diff > 50 && lbIdx < lbTotal - 1) goLb(lbIdx + 1);
          else if (diff < -50 && lbIdx > 0) goLb(lbIdx - 1);
          else goLb(lbIdx);
        }, { passive: true });
      }
    })();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('[data-product-features]');
    if (s) initSection(s);
  });
})();